// src/context/NavigatorContext.js
// Mirrors PatientContext's pattern (cached identity + a live-fetched
// profile) but for staff sessions (Navigator/Admin/Support Staff/Doctor).
// Exists so the shell can
// read the caller's own portal-appearance preference (theme) without every
// page re-fetching /navigators/me for itself.
//
// Mounted globally in App.js, same as PatientProvider/SubscriberProvider —
// not gated by role at the provider level, it simply has nothing to fetch
// for a patient/subscriber session (no previu_token, or a token with no
// staff record behind it).

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { navigatorAPI } from '../services/api';
import { useAuth } from './AuthContext';

const NavigatorContext = createContext(null);

export function NavigatorProvider({ children }) {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);

  // Doctor accounts have their own row in the same `navigators` table (see
  // Navigator.java's "identity duality" note), so their theme preference is
  // read and saved exactly like a navigator's.
  const isStaff = !!(user && user.role);

  const refreshProfile = useCallback(() => {
    if (!isStaff) { setProfile(null); return; }
    navigatorAPI.me()
      .then(res => setProfile(res.data))
      // Silent by design, same as PatientContext's own refreshProfile: the
      // shell falls back to the DARK default below, and a failed fetch must
      // not block the portal.
      .catch(() => { /* keep whatever we already had */ });
  }, [isStaff]);

  useEffect(() => { refreshProfile(); }, [refreshProfile]);

  // DARK is the default — matches Navigator.java's entity default (V141) —
  // so the shell renders dark immediately on first paint, before the
  // profile fetch above resolves, rather than flashing light first.
  const theme = profile?.theme === 'LIGHT' ? 'LIGHT' : 'DARK';

  return (
    <NavigatorContext.Provider value={{ profile, theme, refreshProfile }}>
      {children}
    </NavigatorContext.Provider>
  );
}

export function useNavigatorProfile() {
  return useContext(NavigatorContext);
}
